/**
 * Session-expiry return path (AUTH-002 recovery, critical-journey edge case 3).
 *
 * When the Supabase session expires mid-task (token refresh fails and the auth
 * listener fires SIGNED_OUT without the user asking for it), we stash the route
 * the user was on. After the next successful sign-in the login screen sends
 * them straight back, so together with `useFormPersistence` drafts nothing is
 * lost — the login screen also uses the presence of the stash to show
 * "Din session gick ut. Logga in igen — dina uppgifter är sparade."
 *
 * Pure module (no React) so the stash/restore rules are unit-testable.
 * sessionStorage on purpose: the stash should not outlive the browser tab.
 */

export const AUTH_RETURN_TO_KEY = 'auth-return-to';

/** Routes that must never be a post-login destination. */
const EXCLUDED_PREFIXES = ['/login', '/signup', '/auth', '/logout'];

function getStore(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Only same-app paths are ever stashed or replayed: must start with a single
 * "/" (so no `//evil.example` protocol-relative or `https://` absolute URLs)
 * and must not point at an auth screen or the root splash.
 */
export function isStashablePath(path: string): boolean {
  if (typeof path !== 'string' || path.length === 0) return false;
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  if (path.includes('://')) return false;
  if (path === '/') return false;
  return !EXCLUDED_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`),
  );
}

/** Stash the route to return to after re-auth. Invalid paths are ignored. */
export function stashReturnTo(path: string): void {
  if (!isStashablePath(path)) return;
  try {
    getStore()?.setItem(AUTH_RETURN_TO_KEY, path);
  } catch {
    /* storage unavailable — recovery degrades to the default dashboard */
  }
}

/** Read the stashed route without clearing it (validated on the way out too). */
export function peekReturnTo(): string | null {
  try {
    const raw = getStore()?.getItem(AUTH_RETURN_TO_KEY) ?? null;
    return raw && isStashablePath(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Read and clear the stashed route in one step. */
export function consumeReturnTo(): string | null {
  const path = peekReturnTo();
  clearReturnTo();
  return path;
}

export function clearReturnTo(): void {
  try {
    getStore()?.removeItem(AUTH_RETURN_TO_KEY);
  } catch {
    /* ignore */
  }
}
